import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';
import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@mainstage/core-plugin-api';

export interface WorkflowSummary {
  id: string;
  label: string;
  description?: string;
  subjectType: string;
  configurationCount: number;
  activeInstances: number;
  disabledInstances: number;
}

export interface WorkflowDetail extends WorkflowSummary {
  contractVersion: number;
  defaultDefinition: string;
  requiredTerminalOutcomes: Array<'success' | 'failure'>;
  subject: Record<string, unknown>;
  activationScopeSchema: Record<string, unknown>;
  activationScopeFilters: Array<{
    path: string;
    label: string;
    source: 'system-types' | 'environments';
    required?: boolean;
  }>;
  macroPhases: Record<string, unknown> | null;
  triggers: Record<string, unknown>[];
  preconditions: Record<string, unknown>[];
  actions: Record<string, unknown>[];
  eventPolicy: Record<string, unknown> | null;
}

export type WorkflowConfigurationLifecycle =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'RETIRED'
  | 'ARCHIVED';

export type WorkflowConfigurationFilterLifecycle =
  | 'ACTIVE'
  | 'ARCHIVED'
  | 'ALL';

export interface WorkflowConfigurationVersionSummary {
  id: string;
  version: number;
  lifecycleStatus: WorkflowConfigurationLifecycle;
  definitionSchema: string;
  capabilityContractVersion: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  retiredAt: string | null;
}

export interface WorkflowConfigurationVersionDetail
  extends WorkflowConfigurationVersionSummary {
  configurationId: string;
  definitionYaml: string;
}

export interface WorkflowConfigurationVersionListItem
  extends WorkflowConfigurationVersionSummary {
  instanceCount: number;
  pendingInstanceCount: number;
  completedInstanceCount: number;
  blockedActionCount: number;
}

export interface WorkflowConfigurationSummary {
  id: string;
  workflowId: string;
  name: string;
  description: string | null;
  enabled: boolean;
  lifecycleStatus: WorkflowConfigurationFilterLifecycle;
  priority: number;
  activationScope: Record<string, unknown> | null;
  activeVersion: WorkflowConfigurationVersionSummary | null;
  draftVersion: WorkflowConfigurationVersionSummary | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowConfigurationDetail
  extends WorkflowConfigurationSummary {
  versionCount: number;
}

export interface CreateWorkflowConfigurationRequest {
  name: string;
  description?: string | null;
  priority: number;
  definitionYaml?: string;
  activationScope?: Record<string, unknown>;
}

export interface WorkflowManagementApiProblem {
  path: string;
  message: string;
}

export class WorkflowManagementApiError extends Error {
  constructor(
    message: string,
    readonly problems: WorkflowManagementApiProblem[],
    readonly solution?: string,
  ) {
    super(message);
    this.name = 'WorkflowManagementApiError';
  }
}

async function handleWorkflowManagementFailedResponse(
  response: Response,
): Promise<void> {
  const fallbackResponse = response.clone();
  try {
    const payload = await response.json();
    const error = payload?.error;
    if (error && Array.isArray(error.problems)) {
      throw new WorkflowManagementApiError(
        error.message ?? 'The workflow configuration is invalid.',
        error.problems,
        error.solution,
      );
    }
  } catch (error) {
    if (error instanceof WorkflowManagementApiError) throw error;
  }
  await handleFailedResponse(fallbackResponse);
}

interface Pagination {
  offset: number;
  limit: number;
  totalCount: number;
}

export interface WorkflowManagementApi {
  listWorkflows(): Promise<{ items: WorkflowSummary[] }>;
  getWorkflow(workflowId: string): Promise<WorkflowDetail>;
  listConfigurations(
    workflowId: string,
    lifecycle?: WorkflowConfigurationFilterLifecycle,
    offset?: number,
    limit?: number,
  ): Promise<{ items: WorkflowConfigurationSummary[]; pagination: Pagination }>;
  getConfiguration(
    workflowId: string,
    configurationId: string,
  ): Promise<WorkflowConfigurationDetail>;
  listConfigurationVersions(
    workflowId: string,
    configurationId: string,
    offset?: number,
    limit?: number,
  ): Promise<{
    items: WorkflowConfigurationVersionListItem[];
    pagination: Pagination;
  }>;
  createConfiguration(
    workflowId: string,
    request: CreateWorkflowConfigurationRequest,
  ): Promise<WorkflowConfigurationDetail>;
  updateConfigurationMetadata(
    workflowId: string,
    configurationId: string,
    request: {
      name?: string;
      description?: string | null;
      enabled?: boolean;
      priority?: number;
      activationScope?: Record<string, unknown>;
    },
  ): Promise<WorkflowConfigurationDetail>;
  deleteConfiguration(
    workflowId: string,
    configurationId: string,
  ): Promise<void>;
  purgeConfiguration(
    workflowId: string,
    configurationId: string,
  ): Promise<void>;
  getConfigurationVersion(
    workflowId: string,
    configurationId: string,
    versionId: string,
  ): Promise<WorkflowConfigurationVersionDetail>;
  createConfigurationDraft(
    workflowId: string,
    configurationId: string,
    request?: {
      sourceVersionId?: string;
      definitionYaml?: string;
    },
  ): Promise<WorkflowConfigurationVersionDetail>;
  updateConfigurationDraft(
    workflowId: string,
    configurationId: string,
    versionId: string,
    request: {
      definitionYaml?: string;
    },
  ): Promise<WorkflowConfigurationVersionDetail>;
  deleteConfigurationDraft(
    workflowId: string,
    configurationId: string,
    versionId: string,
  ): Promise<void>;
  publishConfigurationDraft(
    workflowId: string,
    configurationId: string,
    versionId: string,
  ): Promise<WorkflowConfigurationVersionDetail>;
}

export const workflowManagementApiRef = createApiRef<WorkflowManagementApi>({
  id: 'wb-workflow-management',
});

export class WorkflowManagementClient implements WorkflowManagementApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async listWorkflows(): Promise<{ items: WorkflowSummary[] }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/workflows`);
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
    return response.json();
  }

  async getWorkflow(workflowId: string): Promise<WorkflowDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(workflowId)}`,
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
    return response.json();
  }

  async listConfigurations(
    workflowId: string,
    lifecycle: WorkflowConfigurationFilterLifecycle = 'ACTIVE',
    offset = 0,
    limit = 25,
  ): Promise<{
    items: WorkflowConfigurationSummary[];
    pagination: Pagination;
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations?offset=${offset}&limit=${limit}&lifecycle=${lifecycle}`,
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
    return response.json();
  }

  async createConfiguration(
    workflowId: string,
    request: CreateWorkflowConfigurationRequest,
  ): Promise<WorkflowConfigurationDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
    return response.json();
  }

  async getConfiguration(
    workflowId: string,
    configurationId: string,
  ): Promise<WorkflowConfigurationDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(configurationId)}`,
    );
    if (!response.ok) await handleFailedResponse(response);
    return response.json();
  }

  async listConfigurationVersions(
    workflowId: string,
    configurationId: string,
    offset = 0,
    limit = 25,
  ): Promise<{
    items: WorkflowConfigurationVersionListItem[];
    pagination: Pagination;
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(
        configurationId,
      )}/versions?offset=${offset}&limit=${limit}`,
    );
    if (!response.ok) await handleFailedResponse(response);
    return response.json();
  }

  async updateConfigurationMetadata(
    workflowId: string,
    configurationId: string,
    request: {
      name?: string;
      description?: string | null;
      enabled?: boolean;
    },
  ): Promise<WorkflowConfigurationDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(configurationId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
    return response.json();
  }

  async deleteConfiguration(
    workflowId: string,
    configurationId: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(configurationId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) await handleFailedResponse(response);
  }

  async purgeConfiguration(
    workflowId: string,
    configurationId: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(configurationId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
  }

  async getConfigurationVersion(
    workflowId: string,
    configurationId: string,
    versionId: string,
  ): Promise<WorkflowConfigurationVersionDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(
        configurationId,
      )}/versions/${encodeURIComponent(versionId)}`,
    );
    if (!response.ok) await handleFailedResponse(response);
    return response.json();
  }

  async createConfigurationDraft(
    workflowId: string,
    configurationId: string,
    request?: {
      sourceVersionId?: string;
      definitionYaml?: string;
      activationScope?: Record<string, unknown>;
      priority?: number;
    },
  ): Promise<WorkflowConfigurationVersionDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(configurationId)}/versions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request ?? {}),
      },
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
    return response.json();
  }

  async updateConfigurationDraft(
    workflowId: string,
    configurationId: string,
    versionId: string,
    request: {
      definitionYaml?: string;
      activationScope?: Record<string, unknown>;
      priority?: number;
    },
  ): Promise<WorkflowConfigurationVersionDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(
        configurationId,
      )}/versions/${encodeURIComponent(versionId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
    return response.json();
  }

  async deleteConfigurationDraft(
    workflowId: string,
    configurationId: string,
    versionId: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(
        configurationId,
      )}/versions/${encodeURIComponent(versionId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) await handleWorkflowManagementFailedResponse(response);
  }

  async publishConfigurationDraft(
    workflowId: string,
    configurationId: string,
    versionId: string,
  ): Promise<WorkflowConfigurationVersionDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('workflow-engine');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/workflows/${encodeURIComponent(
        workflowId,
      )}/configurations/${encodeURIComponent(
        configurationId,
      )}/versions/${encodeURIComponent(versionId)}/publish`,
      { method: 'POST' },
    );
    if (!response.ok) await handleFailedResponse(response);
    return response.json();
  }
}
